import {EducationReportDb, SubCompetenceAssessment} from '../../../../../model/enums/educationReport.model'

export enum FormErrorType {
    empty=1,
    minLength,
    maxLength,
    invalidCharacter
}

export interface FormError {
    key: string
    errorType: FormErrorType,
    errorValue?: number | string
}

export const tooShort = (value: string | undefined, minLength: number): boolean => {
	if (!value) {
		return false
	}
	return value.length < minLength
}

export const exceedsMaxLength = (value: string | undefined, maxLength: number): boolean => {
	if (!value) {
		return false
	}
	return value.length > maxLength
}

const sumValues = (competences: SubCompetenceAssessment[] | undefined): number => {
	let sum = 0
	competences?.forEach(c => {
		if(c?.reason) {
			sum += c.reason.length
		}
	})
	return sum

}

const checkMaxLengthByCompetence = (
	professionalCompetences: SubCompetenceAssessment[] | undefined,
	formErrors: FormError[],
	key: string,
	maxLength: number): void => {
	const totalLength = sumValues(professionalCompetences)
	if (totalLength > maxLength) {
		formErrors.push({
			key: key,
			errorType: FormErrorType.maxLength,
			errorValue: `${totalLength}/${maxLength}`
		})
	}
}

const checkProfessionalCompetencesLength = (assessments: SubCompetenceAssessment[], formErrors: FormError[]):void => {
	const competences = assessments?.filter((sca: SubCompetenceAssessment) =>
		sca.id_sub_competence === 1 ||
        sca.id_sub_competence === 2 ||
        sca.id_sub_competence === 3 ||
        sca.id_sub_competence === 4
	)
	checkMaxLengthByCompetence(competences, formErrors, 'professional_competences', 1000)
}

const checkMethodologicalCompetencesLength = (assessments: SubCompetenceAssessment[], formErrors: FormError[]):void => {
	const competences = assessments?.filter((sca: SubCompetenceAssessment) =>
		sca.id_sub_competence === 5 ||
        sca.id_sub_competence === 6 ||
        sca.id_sub_competence === 7 ||
        sca.id_sub_competence === 8
	)
	checkMaxLengthByCompetence(competences, formErrors, 'methodological_competences', 1000)
}

const checkSocialCompetencesLength = (assessments: SubCompetenceAssessment[], formErrors: FormError[]):void => {
	const competences = assessments?.filter((sca: SubCompetenceAssessment) =>
		sca.id_sub_competence === 9 ||
        sca.id_sub_competence === 10 ||
        sca.id_sub_competence === 11 ||
        sca.id_sub_competence === 12
	)
	checkMaxLengthByCompetence(competences, formErrors, 'social_competences', 1000)
}

const checkSelfCompetencesLength = (assessments: SubCompetenceAssessment[], formErrors: FormError[]):void => {
	const competences = assessments?.filter((sca: SubCompetenceAssessment) =>
		sca.id_sub_competence === 13 ||
        sca.id_sub_competence === 14 ||
        sca.id_sub_competence === 15 ||
        sca.id_sub_competence === 16
	)
	checkMaxLengthByCompetence(competences, formErrors, 'self_competences', 1000)
}

const checkLearnCompetencesLength = (assessments: SubCompetenceAssessment[], formErrors: FormError[]):void => {
	const competences = assessments?.filter((sca: SubCompetenceAssessment) =>
		sca.id_sub_competence === 17 ||
        sca.id_sub_competence === 18
	)
	checkMaxLengthByCompetence(competences, formErrors, 'learn_competences', 500)
}

const checkSchoolPerformanceCompetencesLength = (assessments: SubCompetenceAssessment[], formErrors: FormError[])
    :void => {
	const competences = assessments?.filter((sca: SubCompetenceAssessment) =>
		sca.id_sub_competence === 19 ||
            sca.id_sub_competence === 20 ||
            sca.id_sub_competence === 21
	)
	checkMaxLengthByCompetence(competences, formErrors, 'school_performance_competences', 500)
}

const checkSubCompetences = (educationReport: EducationReportDb, formErrors: FormError[]):void => {
	const assessments = educationReport.sub_competence_assessment
	checkProfessionalCompetencesLength(assessments, formErrors)
	checkMethodologicalCompetencesLength(assessments, formErrors)
	checkSocialCompetencesLength(assessments, formErrors)
	checkSelfCompetencesLength(assessments, formErrors)
	checkLearnCompetencesLength(assessments, formErrors)
	checkSchoolPerformanceCompetencesLength(assessments, formErrors)
}

export const validateEducationReport = (educationReport: EducationReportDb): FormError[] => {

	const formErrors: FormError[] = []
	if(!educationReport) {
		formErrors.push({key:'education_report', errorType:FormErrorType.empty})
		return formErrors
	}

	if (!educationReport.semester_report) {
		formErrors.push({key:'semester_report', errorType:FormErrorType.empty})
	}

	if (exceedsMaxLength(educationReport.feedback_apprenticeship_by_apprentice, 1000)) {
		formErrors.push({key:'feedback_apprenticeship_by_apprentice', errorType:FormErrorType.maxLength})
	}

	if (exceedsMaxLength(educationReport.feedback_responsible_by_apprentice, 1000)) {
		formErrors.push({key:'feedback_responsible_by_apprentice', errorType:FormErrorType.maxLength})
	}

	if (exceedsMaxLength(educationReport.target_achievement_note, 1000)) {
		formErrors.push({key:'target_achievement_note', errorType:FormErrorType.maxLength})
	}

	if (exceedsMaxLength(educationReport.miscellaneous, 500)) {
		formErrors.push({key:'miscellaneous', errorType:FormErrorType.maxLength})
	}

	checkSubCompetences(educationReport, formErrors)

	return formErrors
}